import React, { useEffect, useState, useRef } from 'react';
import JourneyStage from './JourneyStage';
import { Button } from "@/components/ui/button";
import { Plus } from 'lucide-react';
import EmotionCurve from './EmotionCurve';
import { Stage, ContentItem } from '@/types/journey';
import { EmotionLevel } from './EmotionSelector';
import OnboardingGuidelines from './OnboardingGuidelines';

interface JourneyContentProps {
  stages: Stage[];
  onAddStage: () => void;
  onEdit: (stage: Stage) => void;
  onDelete: (id: string) => void;
  onEmotionChange: (id: string, emotion: EmotionLevel) => void;
  onQuoteChange: (id: string, quote: string) => void;
  onTitleChange: (id: string, title: string) => void;
  onDescriptionChange: (id: string, description: string) => void; // Kept for backward compatibility
  onPainPointsChange: (id: string, painPoints: string) => void;
  onOpportunitiesChange: (id: string, opportunities: string) => void;
  onContentUpdate?: (id: string, contentItems: ContentItem[]) => void;
  isMobile?: boolean;
  className?: string;
}

const JourneyContent: React.FC<JourneyContentProps> = ({
  stages,
  onAddStage,
  onEdit,
  onDelete,
  onEmotionChange,
  onQuoteChange,
  onTitleChange,
  onDescriptionChange,
  onPainPointsChange,
  onOpportunitiesChange,
  onContentUpdate,
  isMobile = false,
  className = ''
}) => {
  // State to track common stage height
  const [maxStageHeight, setMaxStageHeight] = useState<number>(600); // Default height
  const stagesContainerRef = useRef<HTMLDivElement>(null);
  
  // Calculate total width needed for all stages
  const stageWidth = 336; // Width of each card
  const stageGap = 24;    // Gap between cards
  const totalWidth = stages.length > 0 
    ? stages.length * (stageWidth + stageGap) + 100 // Extra width for add button
    : 400; // Minimum width when empty
  
  // Calculate stage heights and find the maximum
  useEffect(() => {
    if (stages.length === 0) return;

    // Give a little time for the DOM to update
    const timer = setTimeout(() => {
      const stageElements = document.querySelectorAll('.stage-card');
      
      if (stageElements && stageElements.length > 0) {
        // Get natural heights of all cards
        const heights: number[] = Array.from(stageElements).map(el => {
          // Clone the element to get its natural height
          const clone = el.cloneNode(true) as HTMLElement;
          clone.style.height = 'auto';
          clone.style.position = 'absolute';
          clone.style.visibility = 'hidden';
          document.body.appendChild(clone);
          
          const height = clone.getBoundingClientRect().height;
          document.body.removeChild(clone);
          return height;
        });
        
        // Set the maximum height (add a little padding)
        const maxHeight = Math.max(...heights) + 10;
        setMaxStageHeight(maxHeight);
      }
    }, 200);
    
    return () => clearTimeout(timer);
  }, [stages]);
  
  return (
    <div className={`overflow-x-auto overflow-y-visible bg-card rounded-lg shadow-sm ${isMobile ? 'p-2' : 'p-4'} dark:bg-gray-800 ${className}`}>  
      <div className={`relative bg-white dark:bg-gray-800 rounded-lg shadow-sm ${isMobile ? 'p-2' : 'p-4'} h-auto`}>
        {stages.length === 0 ? (
          <OnboardingGuidelines onAddStage={onAddStage} />
        ) : (
          <div className="relative w-full">
            {/* Wrapper with position: relative to contain the absolutely positioned curve */}
            <div className="relative w-full h-auto">
              {/* Fixed width content container - ensure it's scrollable on mobile */}
              <div 
                className={`${isMobile ? 'overflow-x-auto overflow-y-visible' : 'overflow-y-visible'} pb-4`}
                style={{ 
                  width: isMobile ? 'auto' : totalWidth,
                  position: 'relative'
                }}
              >
                {/* Emotion curve layer */}
                {stages.length > 1 && <EmotionCurve 
                  stages={stages} 
                  fixedHeight={maxStageHeight} 
                />}

                {/* Content layer */}
                <div 
                  ref={stagesContainerRef}
                  className={`flex items-start ${isMobile ? 'pr-6' : ''} gap-6 min-h-full`} 
                  style={isMobile ? { width: totalWidth } : {}}
                >
                  {stages.map((stage, index) => (
                    <JourneyStage 
                      key={stage.id}
                      stage={stage}
                      index={index}
                      onEdit={() => onEdit(stage)}
                      onDelete={() => onDelete(stage.id)}
                      onEmotionChange={(emotion) => onEmotionChange(stage.id, emotion)}
                      onQuoteChange={(quote) => onQuoteChange(stage.id, quote)}
                      onTitleChange={(title) => onTitleChange(stage.id, title)}
                      onDescriptionChange={(description) => onDescriptionChange(stage.id, description)}
                      onPainPointsChange={(painPoints) => onPainPointsChange(stage.id, painPoints)}
                      onOpportunitiesChange={(opportunities) => onOpportunitiesChange(stage.id, opportunities)}
                      onContentUpdate={onContentUpdate ? (contentItems) => onContentUpdate(stage.id, contentItems) : undefined}
                      stageHeight={maxStageHeight} // Pass the maximum height to all stages
                    />
                  ))}
                  
                  <div className="flex items-start justify-center" style={{ height: 'auto' }}>
                    <Button 
                      onClick={onAddStage} 
                      variant="outline" 
                      className={`flex items-center justify-center rounded-full ${isMobile ? 'h-8 w-8 mt-3' : 'h-10 w-10 mt-4'}`}
                      aria-label="Add Stage"
                    >
                      <Plus className={`${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Add a "swipe hint" indicator for mobile */}
      {isMobile && stages.length > 0 && (
        <div className="mt-2 text-center text-xs text-muted-foreground opacity-80">
          ← Swipe to see more stages →
        </div>
      )}
    </div>
  );
};

export default JourneyContent;
